<template>
  <div class="login-container">
    <!-- 新增：登录状态变化提示 -->
    <div v-if="showLoginStatusChangeTip" class="login-status-change-tip">
      <el-alert
        title="登录状态变化，请刷新"
        type="info"
        :closable="false"
        show-icon
      >
        <template #default>
          <p>检测到其他标签页已完成登录，请刷新页面以同步最新状态。</p>
          <div class="tip-actions">
            <el-button type="primary" size="small" @click="refreshPage">
              刷新页面
            </el-button>
            <el-button size="small" @click="dismissTip">
              忽略
            </el-button>
          </div>
        </template>
      </el-alert>
    </div>

    <div class="loading-screen" v-if="!logoLoaded">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <div class="logo-container" :class="{ 'animation-complete': animationComplete }" v-show="logoLoaded">
      <img 
        src="../assets/account.png" 
        alt="Logo" 
        class="logo" 
        ref="logoRef"
        @load="handleLogoLoaded" 
        @error="handleLogoError"
      />
      <h1 class="system-title" :class="{ 'show': animationComplete }">E时代 统一认证中心</h1>
    </div>

    <!-- 登录区域容器 -->
    <div class="desktop-login-wrapper" v-show="logoLoaded">
      <!-- PC 端：统一卡片容器 -->
      <div class="unified-login-card" :class="{ 'show': animationComplete }" v-if="isDesktop">
        <!-- 左侧：登录表单区 -->
        <div class="login-form-section">
          <div class="section-header">
            <h2>登录 / 注册</h2>
          </div>
          <div class="section-content" v-loading="loading">
            <div class="login-form">
              <transition name="email-slide" mode="out-in">
                <div v-if="!emailCodeSent && !showPasswordField" key="method-select" class="email-step">
                  <el-form :model="formData" @keypress.enter="selectPasswordLogin">
                    <el-form-item label="邮箱/用户名">
                      <el-input 
                        v-model="formData.username" 
                        placeholder="邮箱或用户名"
                        clearable
                        @clear="handleClearUsername"
                      >
                        <template #prefix>
                          <el-icon><User /></el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                  </el-form>
                  <div class="login-method-buttons">
                    <el-button 
                      :type="isEmailFormat ? 'primary' : 'default'"
                      @click="startEmailCodeLogin" 
                      :loading="sendingCode"
                      class="method-btn"
                    >
                      <el-icon><Message /></el-icon>
                      邮箱验证码登录
                    </el-button>
                    <el-button 
                      :type="isEmailFormat ? 'default' : 'primary'"
                      @click="selectPasswordLogin"
                      class="method-btn"
                    >
                      <el-icon><Lock /></el-icon>
                      密码登录
                    </el-button>
                    <el-button 
                      @click="handlePasskeyLogin" 
                      :loading="passkeyLoginLoading"
                      class="method-btn"
                    >
                      <el-icon><Key /></el-icon>
                      通行密钥
                    </el-button>
                  </div>
                </div>
                <div v-else-if="!emailCodeSent && showPasswordField" key="password-form" class="email-step">
                  <el-form ref="accountFormRef" :model="formData" :rules="rules" @keypress.enter="handleSubmit">
                    <el-form-item prop="username" label="邮箱/用户名">
                      <el-input 
                        v-model="formData.username" 
                        placeholder="邮箱或用户名"
                        clearable
                        @clear="handleClearUsername"
                      >
                        <template #prefix>
                          <el-icon><User /></el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                    <el-form-item prop="password" label="密码">
                      <el-input 
                        v-model="formData.password" 
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="密码"
                        autofocus
                      >
                        <template #prefix>
                          <el-icon><Lock /></el-icon>
                        </template>
                        <template #suffix>
                          <el-icon class="cursor-pointer" @click="showPassword = !showPassword">
                            <Hide v-if="showPassword" />
                            <View v-else />
                          </el-icon>
                        </template>
                      </el-input>
                    </el-form-item>
                  </el-form>
                </div>
                <div v-else key="pin-code" class="email-step code-step">
                  <div class="code-sent-info">
                    <div class="code-sent-row">
                      <el-icon class="code-sent-icon"><Message /></el-icon>
                      <span class="code-sent-email">{{ emailFormData.email }}</span>
                      <el-button text type="primary" size="small" @click="resetEmailCodeStep">修改</el-button>
                    </div>
                    <span class="code-sent-hint">请输入邮箱收到的6位验证码</span>
                  </div>
                  <div class="pin-code-container">
                    <input
                      v-for="(_, index) in 6"
                      :key="index"
                      :ref="el => setPinRef(el, index)"
                      type="text"
                      inputmode="numeric"
                      maxlength="1"
                      class="pin-code-input"
                      :class="{ 'has-value': emailCodeDigits[index] }"
                      :value="emailCodeDigits[index]"
                      @input="handlePinInput(index, $event)"
                      @keydown="handlePinKeydown(index, $event)"
                      @paste="handlePinPaste($event)"
                      @focus="handlePinFocus(index)"
                    />
                  </div>
                  <div class="code-actions">
                    <span v-if="countdown > 0" class="code-countdown-text">{{ countdown }}秒后可重新获取</span>
                    <el-button v-else text type="primary" size="small" @click="triggerEmailSendCodeCaptcha" :loading="sendingCode">
                      重新获取验证码
                    </el-button>
                  </div>
                </div>
              </transition>
            </div>
            <div class="login-actions" v-if="showPasswordField || emailCodeSent">
              <el-button 
                type="primary" @click="handleSubmit" :loading="loading" class="login-btn">
                登录
              </el-button>
              <div v-if="showPasswordField" class="back-link" @click="backToMethodSelect">
                ← 返回选择登录方式
              </div>
            </div>
            <div class="helper-links">
              <el-button text @click="goToRegister">没有账号？立即注册</el-button>
              <el-button text @click="showPasswordRecoveryTip">忘记密码？</el-button>
            </div>
            <div class="third-party-login">
              <div class="divider"><span>其他登录方式</span></div>
              <div class="third-party-icons">
                <t-icon name="logo-qq" @click="handleQQLogin" />
                <el-icon class="oauth-icon" @click="handleGoogleLogin"><svg viewBox="0 0 48 48" width="1em" height="1em"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657C33.754,6.053,29.131,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,13,24,13c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657 C33.754,6.053,29.131,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.859-1.977,13.409-5.193l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.278-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.794,2.237-2.231,4.166-3.987,5.569 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.988,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg></el-icon>
                <el-icon class="oauth-icon" @click="handleGithubLogin"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12a12 12 0 0 0 8.21 11.44c.6.11.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61c-.55-1.39-1.35-1.76-1.35-1.76c-1.11-.76.08-.75.08-.75c1.22.09 1.86 1.25 1.86 1.25c1.09 1.86 2.86 1.32 3.56 1.01c.11-.79.43-1.32.78-1.62c-2.66-.3-5.47-1.33-5.47-5.93c0-1.31.47-2.38 1.24-3.22c-.12-.3-.54-1.52.12-3.17c0 0 1.01-.32 3.3 1.23c.96-.27 1.98-.4 3-.41c1.02.01 2.04.14 3 .41c2.29-1.55 3.3-1.23 3.3-1.23c.66 1.65.24 2.87.12 3.17c.77.84 1.24 1.91 1.24 3.22c0 4.61-2.81 5.63-5.49 5.92c.44.38.83 1.13.83 2.28v3.38c0 .32.22.69.82.58A12 12 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg></el-icon>
              </div>
            </div>
          </div>
          <!-- 隐藏的极验实例 -->
          <div style="display:none">
            <GeetestCaptcha ref="accountBindGeetestRef" product="bind" :onSuccess="onAccountBindSuccess" />
            <GeetestCaptcha ref="emailBindGeetestRef" product="bind" :onSuccess="onEmailBindSuccess" />
          </div>
        </div>

        <!-- 中间分隔线 -->
        <div class="card-divider">
          <span class="divider-text">或</span>
        </div>

        <!-- 右侧：微信扫码区 -->
        <div class="wechat-scan-section" :class="{ 'show': sidePanelsVisible }">
          <div class="section-header wechat-header">
            <el-icon class="wechat-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="#07C160" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.006-.27-.012-.405-.012v-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg></el-icon>
            <h2>微信扫码登录</h2>
          </div>
          <div class="section-content wechat-content" v-loading="miniprogramLoading">
            <div class="wechat-qr-wrapper">
              <img 
                v-if="miniprogramQrImage"
                :src="miniprogramQrImage" 
                alt="微信小程序扫码登录" 
                class="wechat-qr-code"
              />
              <div v-else class="wechat-qr-placeholder" @click="generateMiniprogramCode">
                <template v-if="miniprogramStatus === 'expired'">
                  <el-icon class="qr-expired-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg></el-icon>
                  <p class="qr-placeholder-text">小程序码已失效</p>
                  <p class="qr-placeholder-subtext">点击重新生成</p>
                </template>
                <template v-else>
                  <el-icon class="qr-placeholder-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.006-.27-.012-.405-.012v-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/></svg></el-icon>
                  <p class="qr-placeholder-text">点击生成小程序码</p>
                </template>
              </div>
            </div>
            <div class="wechat-info">
              <div class="wechat-countdown" v-if="miniprogramCountdown > 0">
                <span class="countdown-text">有效期：{{ formatCountdown(miniprogramCountdown) }}</span>
              </div>
              <div v-if="miniprogramCode" class="wechat-code-display">
                <span class="code-label">识别码：</span>
                <span class="code-value">{{ miniprogramCode.slice(0, 5) }}-{{ miniprogramCode.slice(5, 10) }}</span>
              </div>
              <el-button 
                v-if="miniprogramQrImage && miniprogramCountdown <= 0"
                type="primary" 
                text
                size="small"
                @click="generateMiniprogramCode"
                :loading="miniprogramLoading"
              >
                重新生成
              </el-button>
            </div>
            <p class="wechat-tip">微信扫一扫，确认即可登录</p>
            <!-- 状态提示 -->
            <div class="wechat-status" v-if="miniprogramStatus !== 'waiting'">
              <div v-if="miniprogramStatus === 'confirmed'" class="status-confirmed">
                <el-icon class="status-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg></el-icon>
                <p>授权已确认，正在登录...</p>
              </div>
              <div v-else-if="miniprogramStatus === 'expired'" class="status-expired">
                <p>小程序码已失效</p>
                <el-button type="primary" text size="small" @click="generateMiniprogramCode">重新生成</el-button>
              </div>
              <div v-else-if="miniprogramStatus === 'register'" class="status-register">
                <p class="status-subtitle">该微信未绑定账号</p>
                <p class="register-countdown-text" v-if="registerCountdown > 0">
                  将在 <span class="countdown-number">{{ registerCountdown }}</span> 秒后自动创建
                </p>
                <div class="register-actions-compact">
                  <el-button size="small" @click="cancelRegister">取消</el-button>
                  <el-button type="primary" size="small" @click="confirmRegister" :loading="loading">立即注册</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 移动端：原有登录框 -->
      <div class="login-box" :class="{ 'show': animationComplete }" v-if="!isDesktop">
        <div class="login-header">
          <div class="content-wrapper" :class="{ 'show': animationComplete }">
            <h2>登录 / 注册</h2>
            <div class="login-tabs">
              <span
                :class="['tab', currentMethod === 'account' ? 'active' : '']"
                @click="currentMethod = 'account'"
              >
                登录 / 注册
              </span>
              <span
                :class="['tab', currentMethod === 'miniprogram' ? 'active' : '']"
                @click="currentMethod = 'miniprogram'"
              >
                微信登录
              </span>
            </div>
          </div>
        </div>

      <div class="login-content" :class="{ 'show': animationComplete }" v-loading="loading">
        <transition name="fade" mode="out-in">
          <div v-if="currentMethod === 'account'" key="account" class="login-form">
            <transition name="email-slide" mode="out-in">
              <div v-if="!emailCodeSent && !showPasswordField" key="method-select-mobile" class="email-step">
                <el-form :model="formData" @keypress.enter="selectPasswordLogin">
                  <el-form-item label="邮箱/用户名">
                    <el-input 
                      v-model="formData.username" 
                      placeholder="邮箱或用户名"
                      clearable
                      @clear="handleClearUsername"
                    >
                      <template #prefix>
                        <el-icon><User /></el-icon>
                      </template>
                    </el-input>
                  </el-form-item>
                </el-form>
                <div class="login-method-buttons">
                  <el-button 
                    :type="isEmailFormat ? 'primary' : 'default'"
                    @click="startEmailCodeLogin" 
                    :loading="sendingCode"
                    class="method-btn"
                  >
                    <el-icon><Message /></el-icon>
                    邮箱验证码登录
                  </el-button>
                  <el-button 
                    :type="isEmailFormat ? 'default' : 'primary'"
                    @click="selectPasswordLogin"
                    class="method-btn"
                  >
                    <el-icon><Lock /></el-icon>
                    密码登录
                  </el-button>
                  <el-button 
                    @click="handlePasskeyLogin" 
                    :loading="passkeyLoginLoading"
                    class="method-btn"
                  >
                    <el-icon><Key /></el-icon>
                    通行密钥
                  </el-button>
                </div>
              </div>
              <div v-else-if="!emailCodeSent && showPasswordField" key="password-form-mobile" class="email-step">
                <el-form ref="accountFormRef" :model="formData" :rules="rules" @keypress.enter="handleSubmit">
                  <el-form-item prop="username" label="邮箱/用户名">
                    <el-input 
                      v-model="formData.username" 
                      placeholder="邮箱或用户名"
                      clearable
                      @clear="handleClearUsername"
                    >
                      <template #prefix>
                        <el-icon><User /></el-icon>
                      </template>
                    </el-input>
                  </el-form-item>
                  <el-form-item prop="password" label="密码">
                    <el-input 
                      v-model="formData.password" 
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="密码"
                      autofocus
                    >
                      <template #prefix>
                        <el-icon><Lock /></el-icon>
                      </template>
                      <template #suffix>
                        <el-icon class="cursor-pointer" @click="showPassword = !showPassword">
                          <Hide v-if="showPassword" />
                          <View v-else />
                        </el-icon>
                      </template>
                    </el-input>
                  </el-form-item>
                </el-form>
              </div>
              <div v-else key="pin-code-mobile" class="email-step code-step">
                <div class="code-sent-info">
                  <div class="code-sent-row">
                    <el-icon class="code-sent-icon"><Message /></el-icon>
                    <span class="code-sent-email">{{ emailFormData.email }}</span>
                    <el-button text type="primary" size="small" @click="resetEmailCodeStep">修改</el-button>
                  </div>
                  <span class="code-sent-hint">请输入邮箱收到的6位验证码</span>
                </div>
                <div class="pin-code-container">
                  <input
                    v-for="(_, index) in 6"
                    :key="index"
                    :ref="el => setPinRef(el, index)"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="pin-code-input"
                    :class="{ 'has-value': emailCodeDigits[index] }"
                    :value="emailCodeDigits[index]"
                    @input="handlePinInput(index, $event)"
                    @keydown="handlePinKeydown(index, $event)"
                    @paste="handlePinPaste($event)"
                    @focus="handlePinFocus(index)"
                  />
                </div>
                <div class="code-actions">
                  <span v-if="countdown > 0" class="code-countdown-text">{{ countdown }}秒后可重新获取</span>
                  <el-button v-else text type="primary" size="small" @click="triggerEmailSendCodeCaptcha" :loading="sendingCode">
                    重新获取验证码
                  </el-button>
                </div>
              </div>
            </transition>
          </div>

          <div v-else-if="currentMethod === 'miniprogram'" key="miniprogram" class="miniprogram-form">
            <div class="miniprogram-container">
              <div class="miniprogram-wrapper" v-loading="miniprogramLoading">
                <div class="miniprogram-display">
                  <!-- 小程序二维码 -->
                  <div class="miniprogram-qr-section">
                    <img 
                      v-if="miniprogramQrImage"
                      :src="miniprogramQrImage" 
                      alt="微信小程序扫码登录" 
                      class="miniprogram-qr-code"
                    />
                    <div v-else class="miniprogram-qr-placeholder" @click="generateMiniprogramCode">
                      <template v-if="miniprogramStatus === 'expired'">
                        <el-icon class="qr-expired-icon"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg></el-icon>
                        <p class="qr-placeholder-text">小程序码已失效</p>
                        <p class="qr-placeholder-subtext">点击重新生成</p>
                      </template>
                      <template v-else>
                        <el-icon class="qr-placeholder-icon"><Key /></el-icon>
                        <p class="qr-placeholder-text">点击下方按钮生成小程序码</p>
                      </template>
                  </div>
                    <p class="qr-tip">
                      微信扫一扫，小程序中点“确认”即可完成登录
                    </p>
                    <div class="countdown-display" v-if="miniprogramCountdown > 0">
                      <span class="countdown-text">小程序码有效期：{{ formatCountdown(miniprogramCountdown) }}</span>
                    </div>
                  </div>
                  <!-- 识别码展示 -->
                  <div v-if="miniprogramCode" class="miniprogram-code-box">
                    <span class="code-label">识别码：</span>
                    <span class="code-value">
                      <span class="code-chunk">{{ miniprogramCode.slice(0, 5) }}</span>
                      <span class="code-separator">-</span>
                      <span class="code-chunk">{{ miniprogramCode.slice(5, 10) }}</span>
                    </span>
                  </div>
                  <el-button 
                    type="primary" 
                    @click="generateMiniprogramCode"
                    :disabled="miniprogramCountdown > 0"
                    :loading="miniprogramLoading"
                  >
                    {{ miniprogramCountdown > 0 ? `${formatCountdown(miniprogramCountdown)} 后可重新获取` : (miniprogramQrImage ? '重新生成小程序码' : '生成小程序码') }}
                  </el-button>
                  <!-- 移除内嵌极验，占位验证改为按钮绑定弹窗方式 -->
                  <div 
                    class="miniprogram-status" 
                    v-if="miniprogramStatus !== 'waiting'"
                  >
                    <div v-if="miniprogramStatus === 'confirmed'" class="status-confirmed">
                      <p>授权已确认，正在为你登录...</p>
                    </div>
                    <div v-else-if="miniprogramStatus === 'expired'" class="status-expired">
                      <p>小程序码已失效，请重新生成</p>
                      <el-button type="primary" text @click="generateMiniprogramCode">重新生成</el-button>
                    </div>
                    <div v-else-if="miniprogramStatus === 'register'" class="status-register">
                      <p class="status-subtitle">该微信未绑定账号</p>
                      <p class="register-countdown-text" v-if="registerCountdown > 0">
                        将在 <span class="countdown-number">{{ registerCountdown }}</span> 秒后自动创建新账号
                      </p>
                      <div class="register-actions">
                        <el-button @click="cancelRegister">取消</el-button>
                        <el-button type="primary" @click="confirmRegister" :loading="loading">
                          {{ registerCountdown > 0 ? `立即注册 (${registerCountdown}s)` : '注册新账号' }}
                        </el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </transition>

        <div class="action-buttons" v-if="currentMethod !== 'miniprogram'">
          <div class="login-actions" v-if="showPasswordField || emailCodeSent">
            <el-button 
              type="primary" @click="handleSubmit" :loading="loading" style="width: 100%">
              登录
            </el-button>
            <div v-if="showPasswordField" class="back-link" @click="backToMethodSelect">
              ← 返回选择登录方式
            </div>
          </div>
          <div class="helper-links">
            <el-button text @click="goToRegister">
              没有账号？立即注册
            </el-button>
            <el-button text @click="showPasswordRecoveryTip">忘记密码？</el-button>
          </div>
        </div>

        <div class="third-party-login">
          <div class="divider">
            <span>其他登录方式</span>
          </div>
          <div class="third-party-icons">
            <LogoQqIcon @click="handleQQLogin" />
            <el-icon class="oauth-icon" @click="handleGoogleLogin"><svg viewBox="0 0 48 48" width="1em" height="1em"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657C33.754,6.053,29.131,4,24,4C12.955,4,4,12.955,4,24 s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,13,24,13c3.059,0,5.842,1.154,7.957,3.043l5.657-5.657 C33.754,6.053,29.131,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/><path fill="#4CAF50" d="M24,44c5.166,0,9.859-1.977,13.409-5.193l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.278-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.794,2.237-2.231,4.166-3.987,5.569 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.988,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/></svg></el-icon>
            <el-icon class="oauth-icon" @click="handleGithubLogin"><svg viewBox="0 0 24 24" width="1em" height="1em"><path fill="currentColor" d="M12 0C5.37 0 0 5.37 0 12a12 12 0 0 0 8.21 11.44c.6.11.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61c-.55-1.39-1.35-1.76-1.35-1.76c-1.11-.76.08-.75.08-.75c1.22.09 1.86 1.25 1.86 1.25c1.09 1.86 2.86 1.32 3.56 1.01c.11-.79.43-1.32.78-1.62c-2.66-.3-5.47-1.33-5.47-5.93c0-1.31.47-2.38 1.24-3.22c-.12-.3-.54-1.52.12-3.17c0 0 1.01-.32 3.3 1.23c.96-.27 1.98-.4 3-.41c1.02.01 2.04.14 3 .41c2.29-1.55 3.3-1.23 3.3-1.23c.66 1.65.24 2.87.12 3.17c.77.84 1.24 1.91 1.24 3.22c0 4.61-2.81 5.63-5.49 5.92c.44.38.83 1.13.83 2.28v3.38c0 .32.22.69.82.58A12 12 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg></el-icon>
          </div>
        </div>
      </div>

      <!-- 隐藏的极验实例 -->
      <div style="display:none">
        <GeetestCaptcha ref="accountBindGeetestRef" product="bind" :onSuccess="onAccountBindSuccess" />
        <GeetestCaptcha ref="emailBindGeetestRef" product="bind" :onSuccess="onEmailBindSuccess" />
      </div>
    </div><!-- 移动端登录框结束 -->
    </div><!-- 布局容器结束 -->
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, reactive, onMounted, onBeforeMount, watch, onUnmounted, computed, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/store/user';
import { useSharedStore } from '@/store/shared';
import request from '@/utils/request';
import { User, Lock, Message, Key, View, Hide } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { config } from '@/config'
import GeetestCaptcha from '@/components/GeetestCaptcha.vue';
import { LogoQqIcon } from 'tdesign-icons-vue-next';

const router = useRouter();
const userStore = useUserStore();
const sharedStore = useSharedStore();

// 新增：登录状态变化提示相关
const showLoginStatusChangeTip = ref(false);

// PC 端检测
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
const isDesktop = computed(() => windowWidth.value >= 1024);

// 侧边面板显示控制（动画延迟）
const sidePanelsVisible = ref(false);

const currentMethod = ref('account');

const formData = reactive({
  username: sharedStore.email || '',
  password: '',
});

const emailFormData = reactive({
  email: sharedStore.email || '',
  code: '',
});

const loading = ref(false);
const sendingCode = ref(false);
const passkeyLoginLoading = ref(false);
const showPasswordField = ref(false);

// 小程序扫码登录相关
const miniprogramLoading = ref(false);
const miniprogramStatus = ref('waiting'); // waiting, confirmed, expired, register
const miniprogramScene = ref('');
const miniprogramQrImage = ref('');
const miniprogramCode = ref('');
const miniprogramSecretKey = ref(''); // 客户端绑定密钥，用于轮询时验证身份
const pollTimer = ref<ReturnType<typeof setInterval> | null>(null);
const pendingWechatData = ref<any>(null);
const isGeneratingCode = ref(false); // 防止重复生成验证码
const miniprogramCountdown = ref(0); // 小程序验证码倒计时
const miniprogramCountdownTimer = ref<ReturnType<typeof setInterval> | null>(null);
const registerCountdown = ref(0); // 新用户注册倒计时
const registerCountdownTimer = ref<ReturnType<typeof setInterval> | null>(null);

const rules = {
  username: [
    { required: true, message: '请输入用户名或邮箱', trigger: 'blur' },
    { 
      validator: (rule: any, value: string, callback: any) => {
        if (value.includes('@')) {
          // 如果是邮箱格式，使用邮箱验证
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            callback(new Error('请输入正确的邮箱格式'));
          }
        } else if (value.length < 3) {
          callback(new Error('用户名建议不少于3个字符'));
        }
        callback();
      },
      trigger: 'blur'
    }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度为6-20位', trigger: 'blur' },
  ],
};

const emailRules = {
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' },
  ],
  code: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { len: 6, message: '验证码长度为6位', trigger: 'blur' },
  ],
};

const countdown = ref(0);
const startCountdown = () => {
  countdown.value = 60;
  const timer = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(timer);
    }
  }, 1000);
};



// 旧弹窗方式已移除，改为 bind 触发



// 已不使用的第三方登录占位方法移除

const handleGoogleLogin = () => {
  const state = Math.random().toString(36).slice(2)
  localStorage.setItem('google_login_state', state)
  
  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect)
  }
  
  const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(config.google.clientId)}&redirect_uri=${encodeURIComponent(config.google.redirectUri)}&response_type=code&scope=${encodeURIComponent(config.google.scope)}&state=${encodeURIComponent(state)}`
  window.location.href = url
}

const handleGithubLogin = () => {
  const state = Math.random().toString(36).slice(2)
  localStorage.setItem('github_login_state', state)
  
  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect)
  }
  
  const url = `https://github.com/login/oauth/authorize?client_id=${encodeURIComponent(config.github.clientId)}&redirect_uri=${encodeURIComponent(config.github.redirectUri)}&scope=${encodeURIComponent(config.github.scope)}&state=${encodeURIComponent(state)}`
  window.location.href = url
}

const goToRegister = () => {
  // 保留当前输入的邮箱信息
  if (formData.username && formData.username.includes('@')) {
    sharedStore.setEmail(formData.username);
  }
  router.push('/register');
};

const accountFormRef = ref<FormInstance>();
const emailFormRef = ref<FormInstance>();
// bind 模式隐藏实例引用
const accountBindGeetestRef = ref<InstanceType<typeof GeetestCaptcha>>();
const emailBindGeetestRef = ref<InstanceType<typeof GeetestCaptcha>>();
const emailCodeInputRef = ref<any>();

const emailCodeSent = ref(false);
const emailCodeDigits = ref<string[]>(['', '', '', '', '', '']);
const pinRefs = ref<(HTMLInputElement | null)[]>(new Array(6).fill(null));

const setPinRef = (el: any, index: number) => {
  if (el) pinRefs.value[index] = el as HTMLInputElement;
};

const handlePinInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/\D/g, '');
  emailCodeDigits.value[index] = value.slice(-1);
  emailFormData.code = emailCodeDigits.value.join('');

  if (value && index < 5) {
    nextTick(() => pinRefs.value[index + 1]?.focus());
  }

  if (emailCodeDigits.value.every(d => d) && emailFormData.code.length === 6) {
    nextTick(() => pinRefs.value[5]?.blur());
    setTimeout(() => handleSubmit(), 300);
  }
};

const handlePinKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace') {
    if (!emailCodeDigits.value[index] && index > 0) {
      emailCodeDigits.value[index - 1] = '';
      emailFormData.code = emailCodeDigits.value.join('');
      nextTick(() => pinRefs.value[index - 1]?.focus());
    }
  } else if (event.key === 'ArrowLeft' && index > 0) {
    pinRefs.value[index - 1]?.focus();
  } else if (event.key === 'ArrowRight' && index < 5) {
    pinRefs.value[index + 1]?.focus();
  } else if (event.key === 'Enter') {
    handleSubmit();
  }
};

const handlePinPaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const paste = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6);
  if (!paste) return;

  for (let i = 0; i < 6; i++) {
    emailCodeDigits.value[i] = paste[i] || '';
  }
  emailFormData.code = emailCodeDigits.value.join('');

  nextTick(() => {
    const nextEmpty = emailCodeDigits.value.findIndex(d => !d);
    if (nextEmpty >= 0) {
      pinRefs.value[nextEmpty]?.focus();
    } else {
      pinRefs.value[5]?.focus();
    }
  });

  if (paste.length === 6) {
    setTimeout(() => handleSubmit(), 300);
  }
};

const handlePinFocus = (index: number) => {
  pinRefs.value[index]?.select();
};

const resetEmailCodeStep = () => {
  emailCodeSent.value = false;
  emailCodeDigits.value = ['', '', '', '', '', ''];
  emailFormData.code = '';
  showPasswordField.value = false;
};

const isEmailFormat = computed(() => {
  const val = formData.username;
  return val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
});

const startEmailCodeLogin = () => {
  if (!formData.username) {
    ElMessage.warning('请先输入邮箱地址');
    return;
  }
  if (!isEmailFormat.value) {
    ElMessage.warning('当前输入不是邮箱，如需邮箱验证码登录，请输入邮箱地址');
    return;
  }
  emailFormData.email = formData.username;
  sharedStore.setEmail(formData.username);
  triggerEmailSendCodeCaptcha();
};

// 修复首次进入页面时验证码按钮可能未渲染的问题：在动画完成/容器显示后刷新一次
const refreshCaptchas = () => {
  // bind 模式无需刷新占位
};

// 删除占位相关状态

// 区分邮箱 bind 验证用途：sendCode | login
const emailBindPurpose = ref<'sendCode' | 'login'>('sendCode');

// 触发邮箱验证码发送的 bind 模式验证码
const triggerEmailSendCodeCaptcha = async () => {
  try {
    if (!emailCodeSent.value) {
      if (emailFormRef.value) {
        await emailFormRef.value.validateField('email');
      } else if (!emailFormData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFormData.email)) {
        ElMessage.error('请输入正确的邮箱格式');
        return;
      }
    }
    emailBindPurpose.value = 'sendCode';
    emailBindGeetestRef.value?.show?.();
  } catch (error: any) {
    ElMessage.error(error.message || '验证失败');
  }
};

// bind 成功回调：账号密码登录
const onAccountBindSuccess = async (payload: any) => {
  try {
    loading.value = true;
    await userStore.login({
      username: formData.username,
      password: formData.password,
      lotNumber: payload.lotNumber,
      captchaOutput: payload.captchaOutput,
      passToken: payload.passToken,
      genTime: payload.genTime
    });
    formData.password = '';
    ElMessage.success('登录成功');
    const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
    router.push(redirect.startsWith('/login') ? '/profile' : redirect);
  } catch (error: any) {
    ElMessage.error(error.message || '登录失败');
  } finally {
    loading.value = false;
  }
};

// bind 成功回调：邮箱验证码发送 / 邮箱登录
const onEmailBindSuccess = async (payload: any) => {
  try {
    if (emailBindPurpose.value === 'sendCode') {
      sendingCode.value = true;
      await userStore.sendEmailCodeWithGeetest({
        email: emailFormData.email,
        lotNumber: payload.lotNumber,
        captchaOutput: payload.captchaOutput,
        passToken: payload.passToken,
        genTime: payload.genTime
      });
      ElMessage.success('验证码已发送');
      startCountdown();
      emailCodeSent.value = true;
      emailCodeDigits.value = ['', '', '', '', '', ''];
      emailFormData.code = '';
      setTimeout(() => { pinRefs.value[0]?.focus(); }, 500);
    } else {
      loading.value = true;
      const loginData = {
        email: emailFormData.email,
        code: emailFormData.code,
        lotNumber: payload.lotNumber,
        captchaOutput: payload.captchaOutput,
        passToken: payload.passToken,
        genTime: payload.genTime
      };
      await userStore.loginWithEmail(loginData);
      emailFormData.code = '';
      ElMessage.success('登录成功');
      const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
      router.push(redirect.startsWith('/login') ? '/profile' : redirect);
    }
  } catch (error: any) {
    ElMessage.error(error.message || (emailBindPurpose.value === 'sendCode' ? '发送验证码失败' : '登录失败'));
  } finally {
    if (emailBindPurpose.value === 'sendCode') {
      sendingCode.value = false;
    } else {
      loading.value = false;
    }
    emailBindPurpose.value = 'sendCode';
  }
};

// 通行密钥登录（发现模式，不需要用户名）
const handlePasskeyLogin = async () => {
  try {
    passkeyLoginLoading.value = true
    
    // 1. 开始登录，获取挑战（发现模式，不需要用户名）
    const options = await userStore.startPasskeyLogin()
    
    // 2. 转换选项格式为 WebAuthn API 需要的格式
    // 将 Base64URL 转换为 Base64，然后解码（需要添加填充）
    const base64UrlToBase64 = (base64Url: string): string => {
      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      // 添加填充
      while (base64.length % 4) {
        base64 += '='
      }
      return base64
    }
    
    const publicKeyCredentialRequestOptions: any = {
      challenge: Uint8Array.from(atob(base64UrlToBase64(options.challenge)), c => c.charCodeAt(0)),
      timeout: options.timeout,
      userVerification: options.userVerification || 'preferred'
    }
    
    // 发现模式：不传 allowCredentials，让浏览器自动发现所有可用的通行密钥
    if (options.allowCredentials && options.allowCredentials.length > 0) {
      publicKeyCredentialRequestOptions.allowCredentials = options.allowCredentials.map((cred: any) => ({
        id: Uint8Array.from(atob(base64UrlToBase64(cred.id)), c => c.charCodeAt(0)),
        type: cred.type
      }))
    }
    
    // 3. 调用 WebAuthn API 获取断言
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions
    }) as any
    
    if (!assertion) {
      throw new Error('通行密钥验证失败')
    }
    
    // 4. 转换响应为 Base64URL
    const response = assertion.response as any
    
    // 将 ArrayBuffer 转换为 Base64URL
    const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
      const bytes = new Uint8Array(buffer)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
    }
    
    const assertionBase64 = {
      id: assertion.id, // 已经是 Base64URL
      response: {
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64(response.authenticatorData),
        signature: arrayBufferToBase64(response.signature)
      }
    }
    
    // 5. 完成登录
    await userStore.finishPasskeyLogin(
      options.challenge,
      JSON.stringify(assertionBase64)
    )
    
    ElMessage.success('登录成功')
    formData.password = ''
    const redirect = (router.currentRoute.value.query.redirect as string) || '/profile'
    router.push(redirect.startsWith('/login') ? '/profile' : redirect)
  } catch (error: any) {
    console.error('通行密钥登录失败:', error)
    if (error.name === 'NotAllowedError') {
      ElMessage.error('操作被取消或设备不支持')
    } else if (error.name === 'InvalidStateError') {
      ElMessage.error('通行密钥验证失败，请重试')
    } else {
      // 使用通用错误信息，不显示具体错误原因
      ElMessage.error('无法使用通行密钥登录，可能是设备不支持')
    }
  } finally {
    passkeyLoginLoading.value = false
  }
}

// 生成小程序码（无需极验）
const generateMiniprogramCode = async () => {
    if (isGeneratingCode.value) return;
  try {
    isGeneratingCode.value = true;
    miniprogramLoading.value = true;
    miniprogramStatus.value = 'waiting';
    const response = await request.post('/auth/miniprogram-login/generate');
    const responseData = response as any;
    if (responseData.code === 200 && responseData.data) {
      miniprogramScene.value = responseData.data.scene;
      miniprogramQrImage.value = responseData.data.qrCodeImage || '';
      miniprogramCode.value = responseData.data.verificationCode || responseData.data.scene || '';
      miniprogramSecretKey.value = responseData.data.secretKey || ''; // 保存客户端绑定密钥
      startPolling();
      startMiniprogramCountdown();
    } else {
      ElMessage.error(responseData.message || '生成小程序码失败');
    }
  } catch (error: any) {
    ElMessage.error('生成小程序码失败');
  } finally {
    miniprogramLoading.value = false;
    isGeneratingCode.value = false;
  }
};

const selectPasswordLogin = () => {
  if (!formData.username) {
    ElMessage.warning('请先输入邮箱或用户名');
    return;
  }
  showPasswordField.value = true;
};

const backToMethodSelect = () => {
  showPasswordField.value = false;
  formData.password = '';
};

const handleSubmit = async () => {
  try {
    if (loading.value) return;
    
    if (emailCodeSent.value) {
      if (emailFormData.code.length !== 6) {
        ElMessage.warning('请输入完整的6位验证码');
        return;
      }
      emailBindPurpose.value = 'login';
      emailBindGeetestRef.value?.show?.();
    } else if (showPasswordField.value) {
      if (!accountFormRef.value) return;
      await accountFormRef.value.validate();
      accountBindGeetestRef.value?.show?.();
    }
  } catch (error: any) {
    ElMessage.error({
      message: error.message || '登录失败',
      grouping: true
    });
  }
};

const animationComplete = ref(false);

const emit = defineEmits(['animation-complete'])

const logoLoaded = ref(false);
const logoRef = ref<HTMLImageElement | null>(null);
const logoLoadRetries = ref(0);
const MAX_RETRIES = 3;

const handleLogoLoaded = () => {
  logoLoaded.value = true;
  startLoginAnimation();
};

const handleLogoError = () => {
  logoLoadRetries.value++;
  if (logoLoadRetries.value <= MAX_RETRIES) {
    ElMessage.warning('加载失败，正在重试...');
    // 强制重新加载
    setTimeout(() => {
      if (logoRef.value) {
        // 通过修改DOM元素的src属性来强制重新加载
        const timestamp = Date.now();
        logoRef.value.src = `../assets/account.png?t=${timestamp}`;
      }
    }, 500);
  } else {
    logoLoaded.value = true;
    ElMessage.error('加载失败，请检查图片文件');
    startLoginAnimation();
  }
};

const startLoginAnimation = () => {
  setTimeout(() => {
    animationComplete.value = true;
    setTimeout(() => {
      emit('animation-complete');
      // 动画完成后再次刷新一次验证码，确保容器可见后实例渲染
      setTimeout(refreshCaptchas, 100);
      
      // PC 端：动画完成后延迟显示微信登录面板
      if (isDesktop.value) {
        setTimeout(() => {
          sidePanelsVisible.value = true;
          // 自动生成微信小程序码
          if (!miniprogramQrImage.value && !miniprogramLoading.value) {
            generateMiniprogramCode();
          }
        }, 300);
      }
    }, 1000);
  }, 600);
};

// 监听窗口大小变化
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

// 接收 iframe 内 QQCallback 的消息，完成登录
// 初始状态下，图片未加载完成，显示加载屏幕
onMounted(() => {
  // 添加超时处理，避免用户长时间等待
  setTimeout(() => {
    if (!logoLoaded.value) {
      logoLoaded.value = true;
      ElMessage.warning('图片加载超时，已为您显示页面');
      startLoginAnimation();
    }
  }, 2000);
  // 初次渲染后，延迟刷新验证码实例，避免首次渲染未挂载导致不显示
  // 移除内嵌实例刷新
  
  // 监听窗口大小变化
  window.addEventListener('resize', handleResize);
});

const showPassword = ref(false);

// 新增：刷新页面方法
const refreshPage = () => {
  window.location.reload();
};

// 新增：忽略提示方法
const dismissTip = () => {
  showLoginStatusChangeTip.value = false;
  sharedStore.clearLoginStatusChanged();
};

// 新增：检查登录状态变化
const checkLoginStatusChange = () => {
  if (sharedStore.loginStatusChanged) {
    showLoginStatusChangeTip.value = true;
  }
};

// 新增：初始化登录状态监听
const initLoginStatusListener = () => {
  // 初始化共享状态监听器
  sharedStore.initLoginStatusListener();
  
  // 监听自定义登录状态变化事件
  window.addEventListener('loginStatusChanged', () => {
    // 延迟检查，确保状态已更新
    setTimeout(() => {
      checkLoginStatusChange();
    }, 100);
  });
  
  // 页面可见性变化时检查状态
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // 页面变为可见时，检查登录状态
      setTimeout(() => {
        checkLoginStatusChange();
      }, 100);
    }
  });
};

onBeforeMount(async () => {
  // 检查是否有错误参数（从授权页面跳转过来）
  const route = router.currentRoute.value
  if (route.query.error === 'token_expired') {
    ElMessage.error(route.query.error_description as string || '登录已过期，请重新登录')
  }
  
  if (userStore.token) {
    try {
      await userStore.checkAuth()
      const redirect = (route.query.redirect as string) || '/profile'
      router.replace(redirect)
    } catch (error) {
      // token 无效，清除登录状态
      userStore.logout()
    }
  }
  
  // 新增：初始化登录状态监听
  initLoginStatusListener();
  
  // 新增：检查登录状态变化
  setTimeout(() => {
    checkLoginStatusChange();
  }, 100);
})

const showPasswordRecoveryTip = () => {
  ElMessageBox.alert(
    '请使用邮箱验证码登录后修改密码，或重新注册新账号，如有特别情况，请联系 accountrecovery@qifalab.com',
    '找回密码提示',
    {
      confirmButtonText: '知道了',
      type: 'info'
    }
  ).catch(() => {
    // 用户点击关闭按钮时的处理
    // 这里可以为空，但需要catch来防止报错
  })
}

const handleQQLogin = () => {
  // 生成随机state用于防止CSRF攻击
  const state = Math.random().toString(36).substring(7);
  localStorage.setItem('qq_login_state', state);
  
  // 保存当前的 redirect 参数，以便登录后能回到授权页面
  const redirect = router.currentRoute.value.query.redirect as string;
  if (redirect) {
    localStorage.setItem('oauth_login_redirect', redirect);
  }
  
  // 直接跳转到 QQ 授权页面（不使用 SDK，避免冲突）
  const params = new URLSearchParams({
    response_type: 'token',
    client_id: config.qq.appId,
    redirect_uri: config.qq.redirectUri,
    scope: 'get_user_info',
    state: state
  });
  
  window.location.href = `https://graph.qq.com/oauth2.0/authorize?${params.toString()}`;
};

// 监听username变化，如果是邮箱格式则同步到email字段和共享存储
watch(() => formData.username, (newValue) => {
  if (newValue && newValue.includes('@')) {
    emailFormData.email = newValue;
    sharedStore.setEmail(newValue);
  }
});

// 监听email变化，同步到共享存储
watch(() => emailFormData.email, (newValue) => {
  if (newValue) {
    sharedStore.setEmail(newValue);
  }
});

// 监听登录方式变化：进入小程序登录时自动获取一次小程序码，离开时停止计时/轮询
watch(() => currentMethod.value, (newMethod, oldMethod) => {
  if (newMethod === 'miniprogram') {
    miniprogramStatus.value = 'waiting';
    // 仅在当前没有有效小程序码时自动生成，避免重复刷新
    if (!miniprogramQrImage.value && !miniprogramLoading.value) {
      generateMiniprogramCode();
    }
  } else if (oldMethod === 'miniprogram') {
    stopMiniprogramCountdown();
    stopPolling();
    miniprogramCode.value = '';
    miniprogramQrImage.value = '';
    miniprogramScene.value = '';
    miniprogramStatus.value = 'waiting';
  }
});

const handleClearUsername = () => {
  // 无论用户名是什么格式，都清除共享存储
  sharedStore.clearEmail();
  // 同步清除邮箱登录方式的邮箱
  emailFormData.email = '';
};

const handleClearEmail = () => {
  // 清除共享存储
  sharedStore.clearEmail();
  // 同步清除账号密码登录方式的邮箱
  if (formData.username.includes('@')) {
    formData.username = '';
  }
};

// 小程序扫码登录相关方法：
// 页面加载时不主动生成，由切换到“小程序登录”Tab 时自动生成

const formatCountdown = (seconds: number) => {
  if (seconds <= 0) return '';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min > 0) return `${min}:${sec.toString().padStart(2, '0')}`;
  return `${sec}秒`;
};

// 开始小程序验证码倒计时
const startMiniprogramCountdown = () => {
  miniprogramCountdown.value = 300; // 5分钟倒计时
  miniprogramCountdownTimer.value = setInterval(() => {
    miniprogramCountdown.value--;
    if (miniprogramCountdown.value <= 0) {
      if (miniprogramCountdownTimer.value) {
        clearInterval(miniprogramCountdownTimer.value);
        miniprogramCountdownTimer.value = null;
      }
      miniprogramQrImage.value = '';
      miniprogramCode.value = '';
      miniprogramScene.value = '';
      miniprogramStatus.value = 'expired';
      if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
      }
    }
  }, 1000);
};

// 停止小程序验证码倒计时
const stopMiniprogramCountdown = () => {
  if (miniprogramCountdownTimer.value) {
    clearInterval(miniprogramCountdownTimer.value);
    miniprogramCountdownTimer.value = null;
  }
  miniprogramCountdown.value = 0;
};

// 开始新用户注册倒计时
const startRegisterCountdown = () => {
  stopRegisterCountdown(); // 先清除可能存在的倒计时
  registerCountdown.value = 10; // 10秒倒计时
  registerCountdownTimer.value = setInterval(() => {
    registerCountdown.value--;
    if (registerCountdown.value <= 0) {
      stopRegisterCountdown();
      // 倒计时结束，自动注册
      confirmRegister();
    }
  }, 1000);
};

// 停止新用户注册倒计时
const stopRegisterCountdown = () => {
  if (registerCountdownTimer.value) {
    clearInterval(registerCountdownTimer.value);
    registerCountdownTimer.value = null;
  }
  registerCountdown.value = 0;
};

// bind 模式已直接触发，不需要占位回调

// 小程序码生成逻辑已内聚到 generateMiniprogramCode 中，无需额外占位回调

const startPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
  }
  
  pollTimer.value = setInterval(async () => {
    try {
      const response = await request.get(`/auth/miniprogram-login/status/${miniprogramScene.value}?flow=login&secretKey=${miniprogramSecretKey.value}`);
      
      const responseData = response as any;
      if (responseData.code === 200) {
        const { status, token, user, message } = responseData.data;
        
        if (status === 'confirmed' && token && user) {
          // 登录成功
          miniprogramStatus.value = 'confirmed';
          stopPolling();
          userStore.token = token;
          userStore.userInfo = user;
          localStorage.setItem('token', token);
          
          // 新增：通知其他标签页登录状态变化
          userStore.notifyLoginStatusChange();
          
          ElMessage.success('登录成功');
          const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
          router.push(redirect.startsWith('/login') ? '/profile' : redirect);
        } else if (status === 'expired') {
          miniprogramStatus.value = 'expired';
          miniprogramQrImage.value = ''; // 清空二维码，激活重新获取逻辑
          stopPolling();
        } else if (status === 'register_required') {
          // 需要注册新账号
          miniprogramStatus.value = 'register';
          pendingWechatData.value = { code: miniprogramScene.value };
          stopPolling();
          // 启动10秒倒计时，倒计时结束自动注册
          startRegisterCountdown();
        } else if (status === 'forbidden') {
          stopPolling();
          ElMessage.error(message || '该用户不可用，请联系管理员');
        }
      }
    } catch (error) {
      console.error('轮询登录状态失败:', error);
    }
  }, 5000); // 每5秒轮询一次，减轻服务器压力并避免重复提示
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
  // 停止倒计时
  stopMiniprogramCountdown();
};

const cancelRegister = () => {
  stopRegisterCountdown(); // 停止注册倒计时
  miniprogramStatus.value = 'waiting';
  pendingWechatData.value = null;
  stopMiniprogramCountdown(); // 停止小程序码倒计时
  generateMiniprogramCode(); // 重新生成验证码
};

const confirmRegister = async () => {
  try {
    if (!pendingWechatData.value) {
      ElMessage.error('微信数据缺失');
      return;
    }
    
    // 停止倒计时（无论是手动点击还是自动触发）
    stopRegisterCountdown();
    loading.value = true;
    
    // 调用注册接口
    const response = await request.post('/auth/register/wechat', {
      wechat_data: pendingWechatData.value
    });
    
    const responseData = response as any;
    if (responseData.code === 200) {
      // 注册成功，自动登录
      userStore.token = responseData.data.token;
      userStore.userInfo = responseData.data.user;
      localStorage.setItem('token', responseData.data.token);
      
      // 新增：通知其他标签页登录状态变化
      userStore.notifyLoginStatusChange();
      
      ElMessage.success('注册并登录成功');
      
      const redirect = (router.currentRoute.value.query.redirect as string) || '/profile';
      router.push(redirect.startsWith('/login') ? '/profile' : redirect);
    } else {
      ElMessage.error(responseData.message || '注册失败');
    }
  } catch (error: any) {
    console.error('注册失败:', error);
    ElMessage.error('注册失败');
  } finally {
    loading.value = false;
  }
};

// 页面卸载时清理轮询
onUnmounted(() => {
  stopPolling();
  stopMiniprogramCountdown();
  stopRegisterCountdown(); // 清理注册倒计时
  isGeneratingCode.value = false;
  
  // 清理窗口大小监听器
  window.removeEventListener('resize', handleResize);
  
  // 新增：清理事件监听器
  // 注意：由于使用了匿名函数，这里无法直接移除，但不会造成内存泄漏
});
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--td-bg-color-container);
  overflow: hidden;
  padding: 0 1rem 1rem;
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: var(--td-bg-color-container);
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: var(--el-color-primary);
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.logo-container {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(1.5);
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
              transform 1s cubic-bezier(0.22, 1, 0.36, 1),
              top 1s cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 1;
  width: 100%;
  text-align: center;
  will-change: transform, opacity;
}

.logo-container.animation-complete {
  position: absolute;
  top: 5vh;
  left: 50%;
  transform: translate(-50%, 0) scale(0.55);
  width: 100%;
  text-align: center;

  @media screen and (max-width: 480px) {
    top: 2vh;
    transform: translate(-50%, 0) scale(0.45);
  }
}

.logo {
  width: 150px;
  height: 150px;
  object-fit: contain;
  border-radius: 24px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.login-box {
  width: min(90%, 400px);
  padding: min(5vmin, 32px);
  background: var(--el-bg-color);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 0.2s;
  will-change: transform, opacity;
  position: relative;
  z-index: 2;
  border: 1px solid var(--el-border-color-light);
  margin: 0 auto;
  margin-top: 25vh;
  margin-bottom: 2rem;

  /* 移动端：更紧凑的动画时序 */
  @media screen and (max-width: 1023px) {
    transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: 0.1s;
  }
}

.login-box.show {
  opacity: 1;
  transform: translateY(0);
}

.login-header {
  text-align: center;
  position: relative;
  margin-bottom: min(5vh, 2rem);

  @media screen and (max-width: 480px) {
    margin-bottom: min(3vh, 1rem);
  }
}

.content-wrapper {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 0.5s;
  will-change: transform, opacity;
  /* padding-top: 55px; */

  /* 移动端：更紧凑的动画时序 */
  @media screen and (max-width: 1023px) {
    transition: opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    transition-delay: 0.3s;
  }
}

.content-wrapper.show {
  opacity: 1;
  transform: translateY(0);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.login-tabs {
  display: flex;
  justify-content: center;
  margin-top: min(3vh, 1rem);
  border-bottom: 1px solid var(--td-component-stroke);
}

.tab {
  padding: min(2vmin, 8px) min(4vmin, 16px);
  cursor: pointer;
  margin: 0 min(2vmin, 8px);
  border-bottom: 2px solid transparent;
  color: var(--el-text-color-regular);
}

.tab.active {
  color: var(--el-color-primary);
  border-bottom-color: var(--el-color-primary);
}

.miniprogram-container {
  text-align: center;
  padding: 24px 0;
}

.miniprogram-box {
  margin-bottom: 16px;
}

.miniprogram-box img {
  width: 200px;
  height: 200px;
}

.third-party-login {
  margin-top: min(5vh, 2rem);
}

.divider {
  display: flex;
  align-items: center;
  margin: min(3vh, 1rem) 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--td-component-stroke);
}

.divider span {
  padding: 0 16px;
  color: var(--td-text-color-secondary);
  font-size: 14px;
}

.third-party-icons {
  display: flex;
  justify-content: center;
  gap: min(6vmin, 24px);
  margin-top: min(3vh, 1rem);
}

.third-party-icons :deep(.t-icon) {
  font-size: 24px;
  cursor: pointer;
  color: var(--td-text-color-secondary);
  padding: 8px;
  border-radius: 30%;
  transition: all 0.3s;
}

.third-party-icons :deep(.t-icon:hover) {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  transform: translateY(-2px);
}

.action-buttons {
  margin-top: min(4vh, 1.5rem);
}

.action-buttons .login-actions .el-button--primary {
  height: 42px;
  border-radius: 8px;
  font-size: 15px;
  letter-spacing: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-buttons .login-actions .el-button--primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.35);
}

.action-buttons .login-actions .el-button--primary:active {
  transform: translateY(0);
  box-shadow: none;
}

.helper-links {
  display: flex;
  justify-content: space-between;
  margin-top: min(2vh, 0.75rem);
  flex-wrap: wrap;
  gap: 0.5rem 1rem;

  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
}

.t-form-item {
  margin-bottom: 20px;
}

:deep(.t-form__label) {
  font-size: 14px;
  color: var(--td-text-color-primary);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.tab {
  position: relative;
  transition: color 0.3s ease;
}

.tab::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: var(--td-brand-color);
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: center;
}

.tab.active::after {
  transform: scaleX(1);
}

.t-button {
  transition: all 0.3s ease;
}

.t-button:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.t-button:not(:disabled):active {
  transform: translateY(0);
}

.third-party-icons .t-icon {
  transition: all 0.3s ease;
}

.third-party-icons .t-icon:hover {
  transform: scale(1.1);
}

:deep(.t-input) {
  transition: all 0.3s ease;
}

:deep(.t-input:focus-within) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.login-box {
  animation: none;
}

.t-form-item {
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
}

.login-content.show .t-form-item {
  opacity: 1;
  transform: translateY(0);
  transition-delay: calc(0.1s * var(--index, 0));
}

.t-form-item:nth-child(1) { --index: 1; }
.t-form-item:nth-child(2) { --index: 2; }
.t-form-item:nth-child(3) { --index: 3; }

.verification-code .t-button {
  transition: all 0.3s ease;
}

.verification-code .t-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

:deep(.t-form__controls-content) {
  position: relative;
}

:deep(.t-input--error) {
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

h2 {
  margin: 0;
  padding: 0;
  font-size: 24px;
  line-height: 1.4;
  position: relative;
  z-index: 11;
}

/* Email verification code step transition */
.email-slide-enter-active,
.email-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.email-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.email-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.email-step {
  width: 100%;
}

.code-step {
  text-align: center;
}

.code-sent-info {
  margin-bottom: 8px;
}

.code-sent-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 6px;
}

.code-sent-icon {
  color: var(--el-color-success);
  font-size: 16px;
}

.code-sent-email {
  font-size: 14px;
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.code-sent-hint {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.pin-code-container {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0 16px;
}

.pin-code-input {
  width: 44px;
  height: 52px;
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  border: 2px solid var(--el-border-color);
  border-radius: 10px;
  outline: none;
  background: var(--el-bg-color);
  color: var(--el-text-color-primary);
  transition: all 0.2s ease;
  caret-color: var(--el-color-primary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.pin-code-input:focus {
  border-color: var(--el-color-primary);
  box-shadow: 0 0 0 3px var(--el-color-primary-light-8);
  transform: translateY(-2px);
}

.pin-code-input.has-value {
  border-color: var(--el-color-primary-light-3);
  background: var(--el-color-primary-light-9);
}

.code-step .pin-code-input {
  animation: pin-appear 0.3s ease backwards;
}

.code-step .pin-code-input:nth-child(1) { animation-delay: 0.05s; }
.code-step .pin-code-input:nth-child(2) { animation-delay: 0.1s; }
.code-step .pin-code-input:nth-child(3) { animation-delay: 0.15s; }
.code-step .pin-code-input:nth-child(4) { animation-delay: 0.2s; }
.code-step .pin-code-input:nth-child(5) { animation-delay: 0.25s; }
.code-step .pin-code-input:nth-child(6) { animation-delay: 0.3s; }

@keyframes pin-appear {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.login-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alt-login-btn {
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  letter-spacing: 0.5px;
  margin-left: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.alt-login-btn:hover,
.alt-login-btn:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.alt-login-btn.email-code-btn:hover,
.alt-login-btn.email-code-btn:focus {
  background-color: var(--el-color-primary-light-9);
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}

.alt-login-btn.passkey-btn:hover,
.alt-login-btn.passkey-btn:focus {
  background-color: var(--el-fill-color-light);
  border-color: var(--el-text-color-secondary);
  color: var(--el-text-color-primary);
}

.alt-login-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.email-code-btn {
  width: 100%;
  margin: -8px 0 12px;
}

.passkey-btn {
  width: 100%;
}

.login-method-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.method-btn {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: 0 !important;
}

.method-btn:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

.method-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.method-btn.el-button--primary:hover,
.method-btn.el-button--primary:focus {
  background-color: var(--el-color-primary-dark-2);
  border-color: var(--el-color-primary-dark-2);
  color: #fff;
}

.back-link {
  width: 100%;
  text-align: center;
  font-size: 13px;
  color: var(--el-color-primary);
  cursor: pointer;
  padding: 8px 0;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--el-color-primary-dark-2);
}

/* 邮箱验证码按钮滑入动画 */
.method-slide-enter-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.method-slide-leave-active {
  transition: all 0.25s ease-in;
}

.method-slide-enter-from {
  opacity: 0;
  transform: translateY(-12px);
  max-height: 0;
  margin-bottom: -10px;
}

.method-slide-enter-to {
  opacity: 1;
  transform: translateY(0);
  max-height: 50px;
  margin-bottom: 0;
}

.method-slide-leave-from {
  opacity: 1;
  max-height: 50px;
}

.method-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  max-height: 0;
  margin-bottom: -10px;
}

.qr-expired-icon {
  font-size: 48px;
  color: var(--el-color-warning);
  margin-bottom: 8px;
  opacity: 0.8;
}

.qr-placeholder-subtext {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--el-color-primary);
  cursor: pointer;
}

.code-actions {
  text-align: center;
  min-height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.code-countdown-text {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

@media screen and (max-width: 480px) {
  .pin-code-container {
    gap: 6px;
  }

  .pin-code-input {
    width: 40px;
    height: 48px;
    font-size: 20px;
    border-radius: 8px;
  }

  .code-sent-email {
    font-size: 13px;
    word-break: break-all;
  }
}

@media screen and (max-width: 360px) {
  .pin-code-container {
    gap: 4px;
  }

  .pin-code-input {
    width: 36px;
    height: 44px;
    font-size: 18px;
  }
}

.verification-code {
  display: flex;
  gap: 8px;
  align-items: center;

  @media screen and (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }

  .el-button {
    @media screen and (max-width: 480px) {
      width: 100%;
      margin-top: 0.5rem;
    }
  }
}

.verification-code .t-input,
.verification-code .el-input {
  flex: 1;
}

.verification-code .t-button,
.verification-code .el-button {
  white-space: nowrap;
  min-width: 100px;
}

.third-party-icons .el-icon {
  font-size: 24px;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s;
}

.third-party-icons .el-icon:hover {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  transform: translateY(-2px);
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  box-shadow: none;
  border: 1px solid var(--el-border-color);
}

:deep(.el-input__wrapper:hover) {
  border-color: var(--el-color-primary);
}

:deep(.el-input__wrapper.is-focus) {
  border-color: var(--el-color-primary);
  box-shadow: 0 0 0 1px var(--el-color-primary-light-5);
}

.login-form {
  width: 100%;
}

:deep(.el-form) {
  width: 100%;
}

:deep(.el-form-item) {
  margin-bottom: min(3vh, 1.25rem);
  flex-direction: column;
  align-items: flex-start;
}

:deep(.el-form-item__label) {
  padding: 0 0 8px;
  line-height: 1;
  font-weight: 500;
}

:deep(.el-form-item__content) {
  display: block;
  width: 100%;
  margin-left: 0 !important;
}

:deep(.el-input) {
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

:deep(.el-input__suffix) {
  cursor: pointer;
}

:deep(.el-input__suffix:hover) {
  color: var(--el-color-primary);
}

.system-title {
  position: relative;
  width: 100%;
  text-align: center;
  font-size: min(8vmin, 46px);
  color: var(--el-text-color-primary);
  margin-top: 1rem;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 0.3s;
  will-change: transform, opacity;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  
  @media screen and (max-width: 480px) {
    font-size: max(28px, 6vmin);
    font-weight: 500;
  }
}

.system-title.show {
  opacity: 1;
  transform: translateY(0);
}

/* 小程序验证码登录样式 */
.miniprogram-form {
  padding: 0;
}

.miniprogram-container {
  text-align: center;
}

.miniprogram-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 300px;
  padding: 20px;
  background: var(--el-bg-color-page);
  border-radius: 12px;
  border: 1px solid var(--el-border-color-light);
  margin-bottom: 20px;
}

.miniprogram-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

/* 小程序二维码样式 */
.miniprogram-qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--el-border-color-light);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.miniprogram-qr-placeholder {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 16px;
  transition: background-color 0.3s;
  border-radius: 8px;
}

.miniprogram-qr-placeholder:hover {
  background-color: rgba(64, 158, 255, 0.06);
}

.miniprogram-qr-code {
  width: 160px;
  height: 160px;
  object-fit: contain;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: transform 0.3s ease;
}

.miniprogram-qr-code:hover {
  transform: scale(1.05);
}

.qr-tip {
  margin: 0;
  font-size: 14px;
  color: var(--el-text-color-regular);
  font-weight: 500;
}

.miniprogram-geetest {
  margin: 16px 0;
  display: flex;
  justify-content: center;
}

.captcha-slot {
  position: relative;
  width: 100%;
  min-height: 44px; /* 预留按钮高度，避免布局抖动 */
}
.captcha-skeleton {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
  border: 1px dashed var(--el-border-color);
  border-radius: 4px;
  color: var(--el-text-color-secondary);
}
.captcha-skeleton .skeleton-text {
  font-size: 12px;
}
.captcha-skeleton .skeleton-action {
  font-size: 12px;
}

.countdown-display {
  margin-top: 8px;
  text-align: center;
}

.countdown-text {
  font-size: 12px;
  color: var(--el-color-warning);
  font-weight: 500;
}

/* 小程序登录识别码展示样式 */
.miniprogram-code-box {
  margin-bottom: 16px;
  padding: 8px 12px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
  border: 1px solid var(--el-border-color-light);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.miniprogram-code-box .code-label {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.miniprogram-code-box .code-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.miniprogram-code-box .code-chunk {
  letter-spacing: 1px;
}

.miniprogram-code-box .code-separator {
  color: var(--el-text-color-disabled);
}

.miniprogram-image {
  width: 200px;
  height: 200px;
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  margin-bottom: 20px;
  background: white;
}

.miniprogram-verification-box {
  margin-bottom: 16px;
  padding: 8px 16px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
  border: 1px solid var(--el-border-color-light);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.miniprogram-verification-label {
  font-size: 14px;
  color: var(--el-text-color-regular);
}
.miniprogram-verification-value {
  font-size: 18px;
  font-weight: bold;
  color: var(--el-text-color-primary);
}

.verification-code-first {
  color: var(--el-color-primary);
}

.verification-code-second {
  color: var(--el-color-success);
}

.miniprogram-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}



.miniprogram-status p {
  margin: 0;
  font-size: 14px;
  color: var(--el-text-color-primary);
}

.status-subtitle {
  font-size: 13px !important;
  color: var(--el-text-color-regular) !important;
}

.register-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.register-countdown-text {
  font-size: 14px !important;
  color: var(--el-text-color-secondary) !important;
  margin-top: 8px !important;
}

.countdown-number {
  font-weight: 600;
  font-size: 16px;
  color: var(--el-color-primary);
  display: inline-block;
  min-width: 20px;
  text-align: center;
}

.generate-miniprogram {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
}

.generate-miniprogram .el-icon {
  font-size: 48px;
  color: var(--el-color-info);
}

.generate-miniprogram p {
  margin: 0;
  font-size: 16px;
  color: var(--el-text-color-regular);
}

.miniprogram-tips {
  background: var(--el-color-info-light-9);
  border: 1px solid var(--el-color-info-light-7);
  border-radius: 8px;
  padding: 16px;
  text-align: left;
}

.miniprogram-tips p {
  margin: 0;
  font-size: 14px;
  color: var(--el-text-color-regular);
  line-height: 1.5;
}

.miniprogram-tips p + p {
  margin-top: 4px;
}

@media (max-width: 480px) {
  .miniprogram-image {
    width: 180px;
    height: 180px;
  }
  
  .register-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .register-actions .el-button {
    width: 100%;
  }
}

/* 新增：登录状态变化提示样式 */
.login-status-change-tip {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: 90%;
  max-width: 500px;
}

.tip-actions {
  margin-top: 12px;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.tip-actions .el-button {
  min-width: 80px;
}

/* ========== PC 端统一卡片布局样式 ========== */

/* 布局容器 */
.desktop-login-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 22vh; /* 往下移一点 */
  margin-bottom: 2rem;
  padding: 0 1rem;
}

/* 移动端登录框样式调整 */
.desktop-login-wrapper .login-box {
  margin: 0;
}

/* 平板端调整 */
@media (max-width: 1023px) and (min-width: 481px) {
  .desktop-login-wrapper {
    margin-top: 20vh;
  }
}

/* 小屏手机调整 */
@media (max-width: 480px) {
  .desktop-login-wrapper {
    margin-top: 15vh;
    padding: 0 0.75rem;
  }
}

/* ========== PC 端统一登录卡片 ========== */
.unified-login-card {
  display: flex;
  background: var(--el-bg-color);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--el-border-color-lighter);
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* ease-out，无回弹 */
  transition-delay: 0.5s; /* 等 logo 动画进行一会儿再出现 */
}

.unified-login-card.show {
  opacity: 1;
  transform: translateY(0);
}

/* 左侧登录表单区 */
.login-form-section {
  width: 400px;
  padding: 32px 36px;
  display: flex;
  flex-direction: column;
}

.login-form-section .section-header {
  margin-bottom: 24px;
}

.login-form-section .section-header h2 {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.login-form-section .login-tabs {
  display: flex;
  gap: 20px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  padding-bottom: 12px;
}

.login-form-section .login-tabs .tab {
  font-size: 14px;
  color: var(--el-text-color-regular);
  cursor: pointer;
  padding-bottom: 8px;
  border-bottom: 2px solid transparent;
  margin-bottom: -13px;
  transition: all 0.3s;
}

.login-form-section .login-tabs .tab:hover {
  color: var(--el-color-primary);
}

.login-form-section .login-tabs .tab.active {
  color: var(--el-color-primary);
  border-bottom-color: var(--el-color-primary);
  font-weight: 500;
}

.login-form-section .section-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.login-form-section .login-form {
  flex: 1;
}

.login-form-section .login-actions {
  margin-top: 12px;
}

.login-form-section .login-btn {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-size: 15px;
  letter-spacing: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.login-form-section .login-btn:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.16);
}

.login-form-section .login-btn:active {
  transform: translateY(0);
  box-shadow: none;
}

.login-form-section .login-btn.el-button--primary:hover,
.login-form-section .login-btn.el-button--primary:focus {
  background-color: var(--el-color-primary-dark-2);
  border-color: var(--el-color-primary-dark-2);
  color: #fff;
}

.login-form-section .helper-links {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.login-form-section .third-party-login {
  margin-top: 20px;
  padding-top: 16px;
}

.login-form-section .third-party-login .divider {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.login-form-section .third-party-login .divider::before,
.login-form-section .third-party-login .divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--el-border-color-light);
}

.login-form-section .third-party-login .divider span {
  padding: 0 16px;
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}

.login-form-section .third-party-icons {
  display: flex;
  justify-content: center;
  gap: 24px;
}

.login-form-section .third-party-icons > * {
  font-size: 28px;
  cursor: pointer;
  color: var(--el-text-color-secondary);
  transition: all 0.3s;
}

.login-form-section .third-party-icons > *:hover {
  transform: scale(1.15);
}

/* 中间分隔线 */
.card-divider {
  width: 1px;
  background: var(--el-border-color-light);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-divider .divider-text {
  position: absolute;
  background: var(--el-bg-color);
  padding: 12px 0;
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}

/* 右侧微信扫码区 */
.wechat-scan-section {
  width: 280px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, rgba(7, 193, 96, 0.03) 0%, transparent 100%);
  opacity: 0;
  transform: translateX(20px);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
}

.wechat-scan-section.show {
  opacity: 1;
  transform: translateX(0);
}

.wechat-scan-section .section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
}

.wechat-scan-section .section-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.wechat-scan-section .wechat-icon {
  font-size: 28px;
  color: #07C160;
}

.wechat-scan-section .section-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* 微信二维码样式 */
.wechat-scan-section .wechat-qr-wrapper {
  width: 180px;
  height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  border: 2px solid var(--el-border-color-light);
  overflow: visible;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: all 0.3s;
}

.wechat-scan-section .wechat-qr-wrapper:hover {
  border-color: #07C160;
  box-shadow: 0 6px 24px rgba(7, 193, 96, 0.2);
}

.wechat-scan-section .wechat-qr-code {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 10px;
}

.wechat-scan-section .wechat-qr-wrapper:hover .wechat-qr-code {
  transform: scale(1.5);
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.wechat-scan-section .wechat-qr-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 10px;
  transition: background-color 0.3s;
}

.wechat-scan-section .wechat-qr-placeholder:hover {
  background-color: rgba(7, 193, 96, 0.06);
}

.wechat-scan-section .qr-placeholder-icon {
  font-size: 48px;
  color: #07C160;
  margin-bottom: 8px;
  opacity: 0.7;
}

.wechat-scan-section .qr-placeholder-text {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.wechat-scan-section .wechat-info {
  margin-top: 16px;
  text-align: center;
}

.wechat-scan-section .wechat-countdown .countdown-text {
  font-size: 13px;
  color: var(--el-color-warning);
  font-weight: 500;
}

.wechat-scan-section .wechat-code-display {
  margin-top: 8px;
  padding: 6px 12px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.wechat-scan-section .wechat-code-display .code-label {
  font-size: 12px;
  color: var(--el-text-color-regular);
}

.wechat-scan-section .wechat-code-display .code-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.wechat-scan-section .wechat-tip {
  margin: 16px 0 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  text-align: center;
}

.wechat-scan-section .wechat-status {
  margin-top: 16px;
  text-align: center;
}

.wechat-scan-section .status-confirmed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--el-color-success);
}

.wechat-scan-section .status-confirmed .status-icon {
  font-size: 32px;
}

.wechat-scan-section .status-confirmed p {
  margin: 0;
  font-size: 14px;
}

.wechat-scan-section .status-expired p {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.wechat-scan-section .status-register {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.wechat-scan-section .status-register .status-subtitle {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin: 0;
}

.wechat-scan-section .status-register .register-countdown-text {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin: 0;
}

.wechat-scan-section .register-actions-compact {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

/* ========== 旧的侧边面板样式（保留以防万一） ========== */

/* 微信二维码样式 - 通用 */
.wechat-qr-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 20px;
}

.wechat-qr-wrapper {
  width: 180px;
  height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  border: 2px solid var(--el-border-color-light);
  overflow: visible; /* 允许放大时溢出 */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.wechat-qr-wrapper:hover {
  border-color: #07C160;
  box-shadow: 0 6px 20px rgba(7, 193, 96, 0.15);
}

.wechat-qr-code {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 8px;
}

/* 微信二维码 hover 放大效果 */
.wechat-qr-wrapper:hover .wechat-qr-code {
  transform: scale(1.6);
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.wechat-qr-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s;
  border-radius: 10px;
}

.wechat-qr-placeholder:hover {
  background-color: rgba(7, 193, 96, 0.08);
}

.wechat-qr-placeholder .qr-placeholder-icon {
  font-size: 56px;
  color: #07C160;
  margin-bottom: 10px;
  opacity: 0.8;
}

.wechat-qr-placeholder .qr-placeholder-text {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

/* 微信倒计时 */
.wechat-countdown {
  margin-top: 8px;
}

.wechat-countdown .countdown-text {
  font-size: 12px;
  color: var(--el-color-warning);
  font-weight: 500;
}

/* 微信识别码展示 */
.wechat-code-display {
  margin-top: 8px;
  padding: 6px 12px;
  background: var(--el-fill-color-light);
  border-radius: 4px;
  font-size: 13px;
}

.wechat-code-display .code-label {
  color: var(--el-text-color-secondary);
}

.wechat-code-display .code-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 600;
  color: var(--el-text-color-primary);
  letter-spacing: 1px;
}

/* 微信登录状态 */
.wechat-status {
  margin-top: 12px;
  text-align: center;
  width: 100%;
}

.wechat-status .status-confirmed {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--el-color-success);
}

.wechat-status .status-confirmed .status-icon {
  font-size: 32px;
  animation: checkmark-pop 0.4s ease;
}

@keyframes checkmark-pop {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.wechat-status .status-expired {
  color: var(--el-color-warning);
}

.wechat-status .status-register {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.wechat-status .status-subtitle {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin: 0;
}

.wechat-status .register-countdown-text {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin: 0;
}

.wechat-status .countdown-number {
  font-weight: 600;
  color: var(--el-color-primary);
}

.register-actions-compact {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
</style>
